import React from "react";
import { useTranslation } from "react-i18next";
import { getThemeName, getThemeEmail, isInternationalTheme } from "config/ConfigUtils";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const brand = getThemeName();
  const email = getThemeEmail();
  const isInternational = isInternationalTheme();
  const lastUpdated = "2026-02-03";

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -70; // 20px offset from top
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handlePrint = () => {
    // Save original title and set custom PDF filename
    const originalTitle = document.title;
    const pdfFileName = isInternational ? `${brand}_Privacy_Policy` : `${brand}_Gizlilik_Politikasi`;
    document.title = pdfFileName;

    // Add print styles dynamically
    const printStyles = document.createElement("style");
    printStyles.id = "privacy-print-styles";
    printStyles.textContent = `
      @media print {
        body * { visibility: hidden; }
        #privacy-container, #privacy-container * { visibility: visible; }
        #privacy-container { 
          position: absolute; 
          left: 0; 
          top: 0; 
          width: 100%;
          padding: 20px;
        }
        .no-print { display: none !important; }
      }
    `;
    document.head.appendChild(printStyles);
    window.print();
    // Remove print styles and restore title after printing
    setTimeout(() => {
      document.title = originalTitle;
      const existingStyles = document.getElementById("privacy-print-styles");
      if (existingStyles) existingStyles.remove();
    }, 1000);
  };

  const styles = {
    container: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "40px 24px",
      backgroundColor: "#ffffff",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      color: "#1a1a1a",
      lineHeight: "1.7",
    },
    header: {
      borderBottom: "1px solid #e5e5e5",
      paddingBottom: "24px",
      marginBottom: "32px",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      marginBottom: "8px",
      color: "#111827",
    },
    lastUpdated: {
      fontSize: "14px",
      color: "#6b7280",
      marginBottom: "16px",
    },
    printButton: {
      fontSize: "14px",
      color: "#3b82f6",
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "0",
      textDecoration: "underline",
    },
    toc: {
      backgroundColor: "#f9fafb",
      border: "1px solid #e5e5e5",
      borderRadius: "8px",
      padding: "20px 24px",
      marginBottom: "32px",
    },
    tocTitle: {
      fontSize: "16px",
      fontWeight: "600",
      marginBottom: "12px",
      color: "#374151",
    },
    tocList: {
      listStyle: "none",
      padding: "0",
      margin: "0",
    },
    tocItem: {
      marginBottom: "8px",
    },
    tocLink: {
      fontSize: "14px",
      color: "#3b82f6",
      textDecoration: "none",
      cursor: "pointer",
    },
    section: {
      marginBottom: "32px",
    },
    sectionTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "16px",
      paddingTop: "8px",
    },
    paragraph: {
      fontSize: "15px",
      marginBottom: "12px",
      color: "#374151",
    },
    list: {
      paddingLeft: "20px",
      marginBottom: "12px",
    },
    listItem: {
      fontSize: "15px",
      marginBottom: "8px",
      color: "#374151",
    },
    highlightBox: {
      backgroundColor: "#fef3c7",
      border: "1px solid #fcd34d",
      borderRadius: "8px",
      padding: "16px",
      marginBottom: "16px",
    },
  };

  const tocItems = [
    { id: "introduction", label: t("privacy.toc.introduction") },
    { id: "information", label: t("privacy.toc.information") },
    { id: "usage", label: t("privacy.toc.usage") },
    { id: "sms", label: t("privacy.toc.sms") },
    { id: "sharing", label: t("privacy.toc.sharing") },
    { id: "security", label: t("privacy.toc.security") },
    { id: "children", label: t("privacy.toc.children") },
    { id: "retention", label: t("privacy.toc.retention") },
    { id: "rights", label: t("privacy.toc.rights") },
    { id: "international", label: t("privacy.toc.international") },
    { id: "cookies", label: t("privacy.toc.cookies") },
    { id: "changes", label: t("privacy.toc.changes") },
    { id: "contact", label: t("privacy.toc.contact") },
  ];

  return (
    <div id="privacy-container" style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{t("privacy.privacyPolicy", { brand })}</h1>
        <p style={styles.lastUpdated}>
          {t("privacy.lastUpdated")}: {lastUpdated}
        </p>
        <button style={styles.printButton} onClick={handlePrint} className="no-print">
          {t("privacy.print")}
        </button>
      </div>

      {/* Introduction */}
      <p style={styles.paragraph}>{t("privacy.privacyPolicyIntro", { brand })}</p>

      {/* Table of Contents */}
      <div style={styles.toc}>
        <h2 style={styles.tocTitle}>{t("privacy.tableOfContents")}</h2>
        <ol style={styles.tocList}>
          {tocItems.map((item, index) => (
            <li key={item.id} style={styles.tocItem}>
              <a
                href={`#${item.id}`}
                style={styles.tocLink}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.id);
                }}
              >
                {index + 1}. {item.label}
              </a>
            </li>
          ))}
        </ol>
      </div>

      {/* 1. Introduction */}
      <div id="introduction" style={styles.section}>
        <h2 style={styles.sectionTitle}>1. {t("privacy.toc.introduction")}</h2>
        <p style={styles.paragraph}>{t("privacy.introductionDetails", { brand })}</p>
      </div>

      {/* 2. Information We Collect */}
      <div id="information" style={styles.section}>
        <h2 style={styles.sectionTitle}>2. {t("privacy.toc.information")}</h2>
        <p style={styles.paragraph}>{t("privacy.informationIntro")}</p>

        <p style={{ ...styles.paragraph, fontWeight: "600" }}>{t("privacy.personalDataTitle")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("privacy.dataName")}</li>
          <li style={styles.listItem}>{t("privacy.dataEmail")}</li>
          <li style={styles.listItem}>{t("privacy.dataPhone")}</li>
          <li style={styles.listItem}>{t("privacy.dataAddress")}</li>
        </ul>

        <p style={{ ...styles.paragraph, fontWeight: "600" }}>{t("privacy.locationDataTitle")}</p>
        <p style={styles.paragraph}>{t("privacy.locationDataDetails")}</p>

        <p style={{ ...styles.paragraph, fontWeight: "600" }}>{t("privacy.deviceDataTitle")}</p>
        <p style={styles.paragraph}>{t("privacy.deviceDataDetails")}</p>

        <p style={{ ...styles.paragraph, fontWeight: "600" }}>{t("privacy.usageDataTitle")}</p>
        <p style={styles.paragraph}>{t("privacy.usageData")}</p>
      </div>

      {/* 3. How We Use Information */}
      <div id="usage" style={styles.section}>
        <h2 style={styles.sectionTitle}>3. {t("privacy.toc.usage")}</h2>
        <p style={styles.paragraph}>{t("privacy.usageIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("privacy.usagePurposeService")}</li>
          <li style={styles.listItem}>{t("privacy.usagePurposeNotifications")}</li>
          <li style={styles.listItem}>{t("privacy.usagePurposeImprove")}</li>
          <li style={styles.listItem}>{t("privacy.usagePurposeSecurity")}</li>
          <li style={styles.listItem}>{t("privacy.usagePurposeLegal")}</li>
        </ul>
      </div>

      {/* 4. SMS Communications - CRITICAL FOR TCPA/10DLC */}
      <div id="sms" style={styles.section}>
        <h2 style={styles.sectionTitle}>4. {t("privacy.toc.sms")}</h2>
        <p style={styles.paragraph}>{t("privacy.smsIntro", { brand })}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("privacy.smsTypeService")}</li>
          <li style={styles.listItem}>{t("privacy.smsTypeRoute")}</li>
          <li style={styles.listItem}>{t("privacy.smsTypeVerification")}</li>
          <li style={styles.listItem}>{t("privacy.smsTypeImportant")}</li>
        </ul>
        <p style={styles.paragraph}>{t("privacy.smsFrequency")}</p>
        <p style={styles.paragraph}>{t("privacy.smsOptOut")}</p>
        <p style={styles.paragraph}>{t("privacy.smsNoShare")}</p>
        {isInternational && <p style={styles.paragraph}>{t("privacy.smsTwilio")}</p>}
      </div>

      {/* 5. Information Sharing */}
      <div id="sharing" style={styles.section}>
        <h2 style={styles.sectionTitle}>5. {t("privacy.toc.sharing")}</h2>
        <p style={styles.paragraph}>{t("privacy.noSellUserData")}</p>
        <p style={styles.paragraph}>{t("privacy.sharingIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("privacy.sharingProviders")}</li>
          <li style={styles.listItem}>{t("privacy.sharingLegal")}</li>
          <li style={styles.listItem}>{t("privacy.sharingBusiness")}</li>
        </ul>
      </div>

      {/* 6. Data Security */}
      <div id="security" style={styles.section}>
        <h2 style={styles.sectionTitle}>6. {t("privacy.toc.security")}</h2>
        <p style={styles.paragraph}>{t("privacy.securityMeasures")}</p>
        <p style={styles.paragraph}>{t("privacy.noMethod100Secure")}</p>
      </div>

      {/* 7. Children's Privacy */}
      <div id="children" style={styles.section}>
        <h2 style={styles.sectionTitle}>7. {t("privacy.toc.children")}</h2>
        <p style={styles.paragraph}>{t("privacy.childrenIntro")}</p>
        <p style={styles.paragraph}>{t("privacy.childrenDetails")}</p>
      </div>

      {/* 8. Data Retention */}
      <div id="retention" style={styles.section}>
        <h2 style={styles.sectionTitle}>8. {t("privacy.toc.retention")}</h2>
        <p style={styles.paragraph}>{t("privacy.retentionDetails")}</p>
      </div>

      {/* 9. Your Rights */}
      <div id="rights" style={styles.section}>
        <h2 style={styles.sectionTitle}>9. {t("privacy.toc.rights")}</h2>
        <p style={styles.paragraph}>{t("privacy.rightsIntro")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("privacy.rightAccess")}</li>
          <li style={styles.listItem}>{t("privacy.rightRectify")}</li>
          <li style={styles.listItem}>{t("privacy.rightDelete")}</li>
          <li style={styles.listItem}>{t("privacy.rightPortability")}</li>
          <li style={styles.listItem}>{t("privacy.rightObject")}</li>
        </ul>
        <p style={styles.paragraph}>{t("privacy.contactForRequests")}</p>
      </div>

      {/* 10. International Transfers */}
      <div id="international" style={styles.section}>
        <h2 style={styles.sectionTitle}>10. {t("privacy.toc.international")}</h2>
        <p style={styles.paragraph}>{t("privacy.internationalDetails")}</p>
      </div>

      {/* 11. Cookies */}
      <div id="cookies" style={styles.section}>
        <h2 style={styles.sectionTitle}>11. {t("privacy.toc.cookies")}</h2>
        <p style={styles.paragraph}>{t("privacy.cookiesDetails")}</p>
      </div>

      {/* 12. Changes */}
      <div id="changes" style={styles.section}>
        <h2 style={styles.sectionTitle}>12. {t("privacy.toc.changes")}</h2>
        <p style={styles.paragraph}>{t("privacy.policyUpdates")}</p>
        <p style={styles.paragraph}>{t("privacy.userNotifications")}</p>
      </div>

      {/* 13. Contact */}
      <div id="contact" style={styles.section}>
        <h2 style={styles.sectionTitle}>13. {t("privacy.toc.contact")}</h2>
        <p style={styles.paragraph}>{t("privacy.contactUsAt", { email })}</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
